import { ETrafficVolume } from '@safesystem/client-playbook-lib';
import type { Corridor, Countermeasure } from '#data/safesystem/safecurves/contract.js';
import { aadtFromLabel } from '../Countermeasures/DataAttributes.svelte';

type RecommendationCorridor = Pick<
	Corridor,
	'roadSeal' | 'priorityRating' | 'aadtBand' | 'lengthKm'
>;

/** Project-level treatments, in display order; a countermeasure code is one of these plus an optional paint suffix. */
export const TREATMENTS = [
	'centerline',
	'center-rumble',
	'delineators',
	'edgeline',
	'edge-rumble',
	'rpms'
] as const;
type Treatment = (typeof TREATMENTS)[number];

export const treatmentOf = (code: string) => code.replace(/-(std|hp)$/, '');

export const isPaved = (c: Pick<Corridor, 'roadSeal'>) => c.roadSeal === 1;
/** A project with corridors, none of them paved: only `UNPAVED_TREATMENTS` are offered. */
export const allUnpaved = (corridors: Pick<Corridor, 'roadSeal'>[]) =>
	corridors.length > 0 && !corridors.some(isPaved);
export const UNPAVED_TREATMENTS: readonly Treatment[] = ['delineators'];

// Raw `priorityRating` keys (see `mapPriorityDescription`): HIGH is Priority 1, MEDIUM-HIGH is Priority 2.
export const isP1orP2 = (priority: string) => priority === 'HIGH' || priority === 'MEDIUM-HIGH';

const ALL: readonly Treatment[] = TREATMENTS;

type Rule = (priority: string) => readonly Treatment[];

/** Recommended treatments per corridor, by seal, then volume band, then priority; replace each TBD. */
const RULES: Record<'paved' | 'unpaved', Record<ETrafficVolume, Rule>> = {
	paved: {
		[ETrafficVolume.High]: (p) => (isP1orP2(p) ? ALL : ['centerline', 'delineators', 'edgeline']),
		[ETrafficVolume.Mid]: (p) =>
			isP1orP2(p)
				? ['centerline', 'center-rumble', 'delineators', 'edgeline']
				: ['centerline', 'delineators'],
		[ETrafficVolume.Low]: (p) =>
			isP1orP2(p) ? ['centerline', 'delineators', 'edgeline'] : ['delineators']
	},
	unpaved: {
		[ETrafficVolume.High]: () => ['delineators'],
		[ETrafficVolume.Mid]: () => ['delineators'],
		[ETrafficVolume.Low]: (p) => (isP1orP2(p) ? ['delineators'] : [])
	}
};

// ponytail: a band the playbook doesn't define, or a treatment outside TREATMENTS, falls back to recommended.
export function isRecommended(
	cm: Pick<Countermeasure, 'code'>,
	corridor: RecommendationCorridor
): boolean {
	const treatment = treatmentOf(cm.code);
	const volume = aadtFromLabel(corridor.aadtBand);
	if (!volume || !TREATMENTS.includes(treatment as Treatment)) return true;
	const seal = isPaved(corridor) ? 'paved' : 'unpaved';
	return RULES[seal][volume](corridor.priorityRating).includes(treatment as Treatment);
}

/** SafeCurves recommended length (km) of a project-level countermeasure: the corridors it's recommended on, summed. */
export function recommendedLengthKm(
	cm: Pick<Countermeasure, 'code'>,
	corridors: RecommendationCorridor[]
): number {
	return corridors.reduce((sum, c) => (isRecommended(cm, c) ? sum + c.lengthKm : sum), 0);
}
